import {
  ConnectedPosition,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterViewInit, ElementRef, OnDestroy, Component, Directive, Input } from '@angular/core';
import { scaleInOut } from './tooltip.animations';
import type { PalantirTooltipPosition } from './tooltip.type';

@Component({
  selector: 'palantir-tooltip',
  styleUrls: ['./tooltip.directive.scss'],
  animations: [scaleInOut],
  host: { '[@scaleInOut]': '' },
  template: ` <palantir-text type="white" size="xs" truncate="true">{{ text }}</palantir-text> `,
  standalone: false,
})
export class PalantirTooltipComponent {
  @Input() public text = '';
}

@Directive({
  selector: '[palantirTooltip]',
  exportAs: 'palantirTooltip',
  standalone: false,
})
export class PalantirTooltipDirective implements OnDestroy, AfterViewInit {
  @Input('palantirTooltip') public text: string = '';

  @Input('palantirTooltipPosition') public position?: PalantirTooltipPosition;

  @Input('palantirTooltipDelay') public delay = 0;

  @Input('palantirTooltipDisabled') public disabled = false;

  private overlayRef?: OverlayRef;

  private tooltipPortal = new ComponentPortal(PalantirTooltipComponent);

  private isAbortedWithDelay = false;

  private timerId?: ReturnType<typeof setTimeout>;

  private listeners = [] as { event: string; listener(): void }[];

  public constructor(
    protected readonly overlayPositionBuilder: OverlayPositionBuilder,
    protected readonly elementRef: ElementRef<HTMLElement>,
    protected readonly overlay: Overlay,
  ) {}

  public ngOnDestroy(): void {
    this.clearDelay();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    for (const { event, listener } of this.listeners) {
      this.elementRef.nativeElement?.removeEventListener(event, listener);
    }

    this.overlayRef?.dispose();
  }

  public ngAfterViewInit(): void {
    this.listeners.push({ event: 'mouseenter', listener: this.show.bind(this) });
    this.listeners.push({ event: 'mouseleave', listener: this.hide.bind(this) });
    // eslint-disable-next-line @typescript-eslint/unbound-method
    for (const { event, listener } of this.listeners) {
      this.elementRef.nativeElement?.addEventListener(event, listener);
    }
  }

  public show() {
    if (this.disabled || !this.text || (typeof this.text === 'string' && !this.text?.trim()))
      return;
    this.isAbortedWithDelay = false;

    this.callWithDelay(() => {
      this.overlayRef =
        this.overlayRef ??
        this.overlay.create({
          positionStrategy: this.overlayPositionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions([this.getPosition()]),
          hasBackdrop: false,
          panelClass: 'palantir-tooltip-container',
        });

      if (this.overlayRef.hasAttached()) {
        return;
      }

      const tooltipRef = this.overlayRef.attach(this.tooltipPortal);
      tooltipRef.instance.text = this.text;
    });
  }

  public hide() {
    this.isAbortedWithDelay = true;
    this.overlayRef?.detach();
  }

  private getPosition(): ConnectedPosition {
    const positions: { [key in PalantirTooltipPosition]: ConnectedPosition } = {
      top: { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
      'top-left': { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
      'top-right': { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      bottom: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
      'bottom-left': { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
      'bottom-right': { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      left: { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
      right: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
    };

    return positions[this.position ?? 'bottom'];
  }

  private callWithDelay(fn: () => void) {
    if (this.delay) {
      this.clearDelay();
      this.timerId = setTimeout(() => {
        if (this.isAbortedWithDelay) return;

        fn();
      }, Number(this.delay));

      return;
    }

    fn();
  }

  private clearDelay() {
    clearTimeout(this.timerId);
    this.timerId = undefined;
  }
}
